import { ArrowUp, FileText, Paperclip, Square, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Badge from '../Badge.jsx';
import Button from '../Button.jsx';
import Dropdown from '../Dropdown.jsx';
import Tooltip from '../Tooltip.jsx';
import ChatInputAddMenu from './ChatInputAddMenu.jsx';
import { CHAT_INPUT_MAX_CHARS, defaultAddMenuItems } from './chatInput.constants.js';
import ChatInputDocumentSearch from './ChatInputDocumentSearch.jsx';
import ChatInputFileAttachment from './ChatInputFileAttachment.jsx';
import ChatInputImageAttachment from './ChatInputImageAttachment.jsx';
import ChatInputModelSelector from './ChatInputModelSelector.jsx';
import {
  createFileValidator,
  findAddMenuItemByValue,
  generateAcceptAttribute,
  generateExtensionsMessage,
} from './chatInput.utils.js';

/** Must stay in sync with the textarea scroll wrapper `max-h-[150px]` below. */
const TEXTAREA_AUTOSIZE_MAX_PX = 150;
/** Minimum visible height (~one line, matches `min-h-6` on the textarea). */
const TEXTAREA_AUTOSIZE_MIN_PX = 24;

const CHAT_INPUT_PROCESSING_GRADIENT_VARIANTS = Object.freeze(['bluePurple', 'greenBlue']);

const PROCESSING_GRADIENT_PRESET_CLASS_BY_VARIANT = {
  bluePurple:
    'after:bg-[linear-gradient(60deg,#1d4ed8_0%,#1d4ed8_32%,#9333ea_68%,#9333ea_100%)] dark:after:bg-[linear-gradient(60deg,#3b82f6_0%,#3b82f6_32%,#a855f7_68%,#a855f7_100%)]',
  greenBlue:
    'after:bg-[linear-gradient(60deg,#22c55e_0%,#22c55e_28%,#38bdf8_55%,#2563eb_100%)] dark:after:bg-[linear-gradient(60deg,#4ade80_0%,#4ade80_28%,#38bdf8_55%,#60a5fa_100%)]',
};

const AttachmentItem = memo(function AttachmentItem({ attachment, onAttachmentRemove }) {
  const onClose = useCallback(
    () => onAttachmentRemove?.(attachment.id),
    [attachment.id, onAttachmentRemove]
  );
  if (attachment.type === 'image') {
    return (
      <ChatInputImageAttachment
        imageUrl={attachment.imageUrl}
        isLoading={attachment.isLoading === true}
        onClose={onAttachmentRemove ? onClose : undefined}
      />
    );
  }
  return (
    <ChatInputFileAttachment
      name={attachment.name ?? ''}
      type={attachment.fileType}
      isLoading={attachment.isLoading === true}
      onClose={onAttachmentRemove ? onClose : undefined}
    />
  );
});

const ChatInput = ({
  value: valueProp,
  defaultValue = '',
  placeholder = 'Ask anything',
  growDirection = 'up',
  isProcessing = false,
  onChange,
  onSend,
  onStopClick,
  onClear,
  onAddMenuChange,
  onModeMenuChange,
  onModeTagClose,
  clickableTag = false,
  onModeTagClick,
  addMenuItems = defaultAddMenuItems,
  providers,
  /** When set with `modelSelectorProvider`, controls the model dropdown (sync with host state). */
  modelSelectorValue,
  modelSelectorProvider,
  onModelChange,
  onProviderChange,
  modelAlignMenu = 'right',
  disabled = false,
  className = '',
  id,
  rootRef,
  allowUploadFiles = false,
  attachments = [],
  onUploadClick,
  onAttachmentRemove,
  onFilesSelected,
  onFocus,
  onBlur,
  documents,
  documentSearchProps = {},
  onDocumentSearchMenuChange,
  onDocumentSearchDeleteClick,
  onDocumentSelect,
  allowedFileExtensions = ['pdf', 'doc', 'docx', 'txt'],
  allowImages = true,
  dragDropTitle = 'Drop your files here',
  dragDropSubtitle,
  maxLength = CHAT_INPUT_MAX_CHARS,
  processingGradientVariant = 'bluePurple',
  actionButtonClassName = '',
  /** Extra closable badges after the add menu (e.g. “Add from AIMS Drive” only after modal import). */
  footerBadges = [],
  ...props
}) => {
  const isControlled = valueProp !== undefined;
  const [valueLocal, setValueLocal] = useState(defaultValue);
  const [selectedAddMode, setSelectedAddMode] = useState(null);
  const [selectedMenuValues, setSelectedMenuValues] = useState({});
  const [isDocumentSearchOpen, setIsDocumentSearchOpen] = useState(false);
  const [isDocumentSearchPreloaded, setIsDocumentSearchPreloaded] = useState(false);
  const [documentSearchPosition, setDocumentSearchPosition] = useState({ top: 0, left: 0 });
  const [documentSearchSearchValue, setDocumentSearchSearchValue] = useState(
    documentSearchProps.searchValue ?? ''
  );
  const [isFocused, setIsFocused] = useState(false);
  const [isDragOverFiles, setIsDragOverFiles] = useState(false);

  const articleButtonRef = useRef(null);
  const documentSearchRef = useRef(null);
  const textareaRef = useRef(null);
  const textareaScrollBoxRef = useRef(null);
  const fileInputRef = useRef(null);
  const focusContainerRef = useRef(null);
  const dragCounterRef = useRef(0);
  const value = isControlled ? valueProp : valueLocal;

  const characterCount = (value ?? '').length;
  const hasCharacterLimit = maxLength !== -1;
  const isAtOrExceededLimit = hasCharacterLimit && characterCount >= maxLength;
  const isApproachingLimit =
    hasCharacterLimit && !isAtOrExceededLimit && characterCount >= maxLength * 0.9;
  const hasContent = (value ?? '').length > 0;

  const processingGradientPresetClassName =
    PROCESSING_GRADIENT_PRESET_CLASS_BY_VARIANT[processingGradientVariant] ??
    PROCESSING_GRADIENT_PRESET_CLASS_BY_VARIANT.bluePurple;

  const isAllowedFile = useMemo(
    () => createFileValidator(allowedFileExtensions, allowImages),
    [allowedFileExtensions, allowImages]
  );
  const acceptAttribute = useMemo(
    () => generateAcceptAttribute(allowedFileExtensions, allowImages),
    [allowedFileExtensions, allowImages]
  );
  const dragDropSubtitleText = useMemo(() => {
    if (dragDropSubtitle !== undefined) return dragDropSubtitle;
    return generateExtensionsMessage(allowedFileExtensions, allowImages);
  }, [dragDropSubtitle, allowedFileExtensions, allowImages]);

  const hasDocumentSearch = documents != null && Array.isArray(documents);
  const docSearchSearch = documentSearchProps.searchValue ?? documentSearchSearchValue;
  const setDocSearchSearch = documentSearchProps.onSearchChange ?? setDocumentSearchSearchValue;

  useEffect(() => {
    if (!isDocumentSearchOpen) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') setIsDocumentSearchOpen(false);
    };
    const onMouseDown = (event) => {
      const inPanel = documentSearchRef.current?.contains(event.target);
      const inButton = articleButtonRef.current?.contains(event.target);
      if (!inPanel && !inButton) setIsDocumentSearchOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('mousedown', onMouseDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [isDocumentSearchOpen]);

  const syncTextareaHeight = useCallback(() => {
    const element = textareaRef.current;
    if (!element) return;
    // Collapse before measuring so scrollHeight reflects content, not a stretched flex parent
    // (re-opening the assistant panel used to leave height: auto + huge scrollHeight).
    element.style.height = '0px';
    const intrinsic = element.scrollHeight;
    const clamped = Math.min(intrinsic, TEXTAREA_AUTOSIZE_MAX_PX);
    element.style.height = `${Math.max(clamped, TEXTAREA_AUTOSIZE_MIN_PX)}px`;
  }, []);

  useLayoutEffect(() => {
    syncTextareaHeight();
  }, [value, syncTextareaHeight]);

  useLayoutEffect(() => {
    const box = textareaScrollBoxRef.current;
    if (!box || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      syncTextareaHeight();
    });
    ro.observe(box);
    return () => ro.disconnect();
  }, [syncTextareaHeight]);

  const handleChange = useCallback(
    (event) => {
      let next = event.target.value;
      if (hasCharacterLimit && next.length > maxLength) {
        next = next.slice(0, maxLength);
      }
      if (!isControlled) setValueLocal(next);
      onChange?.(next, event);
    },
    [hasCharacterLimit, isControlled, maxLength, onChange]
  );

  const handleClear = useCallback(() => {
    if (!isControlled) setValueLocal('');
    onChange?.('', { target: { value: '' } });
    onClear?.();
  }, [isControlled, onChange, onClear]);

  const handleSend = () => {
    if (disabled || !value?.trim() || isAtOrExceededLimit) return;
    onSend?.(value.trim());
    if (!isControlled) setValueLocal('');
  };

  const handleActionClick = () => {
    if (isProcessing) onStopClick?.();
    else handleSend();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (!isProcessing) handleSend();
    }
  };

  const handleFocus = useCallback(
    (event) => {
      setIsFocused(true);
      onFocus?.(event);
    },
    [onFocus]
  );

  const handleBlur = useCallback(
    (event) => {
      if (!focusContainerRef.current?.contains(event.relatedTarget)) setIsFocused(false);
      onBlur?.(event);
    },
    [onBlur]
  );

  const isFileDragEvent = (event) => {
    try {
      const types = event?.dataTransfer?.types;
      if (!types) return false;
      return typeof types.includes === 'function'
        ? types.includes('Files')
        : Array.from(types).includes('Files');
    } catch {
      return false;
    }
  };

  const handleDragEnter = useCallback(
    (event) => {
      if (!allowUploadFiles || disabled || !isFileDragEvent(event)) return;
      event.preventDefault();
      dragCounterRef.current += 1;
      setIsDragOverFiles(true);
    },
    [allowUploadFiles, disabled]
  );
  const handleDragOver = useCallback(
    (event) => {
      if (!allowUploadFiles || disabled || !isFileDragEvent(event)) return;
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    },
    [allowUploadFiles, disabled]
  );
  const handleDragLeave = useCallback(
    (event) => {
      if (!allowUploadFiles || disabled || !isFileDragEvent(event)) return;
      event.preventDefault();
      dragCounterRef.current = Math.max(0, dragCounterRef.current - 1);
      if (dragCounterRef.current === 0) setIsDragOverFiles(false);
    },
    [allowUploadFiles, disabled]
  );
  const handleDrop = useCallback(
    (event) => {
      if (!allowUploadFiles || disabled || !isFileDragEvent(event)) return;
      event.preventDefault();
      const files = Array.from(event.dataTransfer?.files ?? []);
      dragCounterRef.current = 0;
      setIsDragOverFiles(false);
      const allowedFiles = files.filter(isAllowedFile);
      if (allowedFiles.length) onFilesSelected?.(allowedFiles);
    },
    [allowUploadFiles, disabled, isAllowedFile, onFilesSelected]
  );

  const handleFileChange = (event) => {
    const input = event?.target;
    if (!input) return;
    const files = Array.from(input.files ?? []);
    if (files.length === 0) return;
    const allowedFiles = files.filter(isAllowedFile);
    if (allowedFiles.length > 0) onFilesSelected?.(allowedFiles);
    input.value = '';
  };

  const handleAddMenuChangeWithMode = useCallback(
    (selectedValue, itemData) => {
      if (allowUploadFiles && selectedValue === 'upload') {
        onUploadClick?.();
        setTimeout(() => fileInputRef.current?.click?.(), 50);
        onAddMenuChange?.(selectedValue, itemData);
        return;
      }

      const item = findAddMenuItemByValue(addMenuItems, selectedValue);
      const shouldShowTag = item?.showTag === true || item?.tagColor != null;
      if (shouldShowTag) {
        const menu = Array.isArray(item?.menu) ? item.menu : undefined;
        const defaultMenuValue = item?.defaultMenuValue ?? menu?.[0]?.value;
        setSelectedAddMode({
          value: selectedValue,
          label: itemData?.label ?? item?.text ?? selectedValue,
          tagColor: item?.tagColor ?? 'success',
          menu,
          menuLabel: item?.menuLabel,
          menuType: item?.menuType ?? 'purple',
        });
        if (menu?.length) {
          setSelectedMenuValues((prev) => ({ ...prev, [selectedValue]: defaultMenuValue }));
        }
      } else {
        setSelectedAddMode(null);
      }
      onAddMenuChange?.(selectedValue, itemData);
    },
    [addMenuItems, allowUploadFiles, onAddMenuChange, onUploadClick]
  );

  const handleModeTagClose = useCallback(() => {
    if (selectedAddMode) {
      const modeValue = selectedAddMode.value;
      onModeTagClose?.(modeValue, selectedMenuValues[modeValue]);
    }
    setSelectedAddMode(null);
  }, [onModeTagClose, selectedAddMode, selectedMenuValues]);

  const handleModeTagClick = useCallback(
    (event) => {
      if (!selectedAddMode) return;
      const modeValue = selectedAddMode.value;
      onModeTagClick?.(event, modeValue, selectedMenuValues[modeValue]);
    },
    [onModeTagClick, selectedAddMode, selectedMenuValues]
  );

  const handleAddMenuSubmenuChange = useCallback(
    (modeValue, selectedValue, itemData) => {
      setSelectedMenuValues((prev) => ({ ...prev, [modeValue]: selectedValue }));
      onModeMenuChange?.(modeValue, selectedValue, itemData);
    },
    [onModeMenuChange]
  );

  const handleArticleMouseEnter = useCallback(() => {
    if (!hasDocumentSearch) return;
    setIsDocumentSearchPreloaded(true);
    const rect = articleButtonRef.current?.getBoundingClientRect();
    if (!rect) return;
    const panelWidth = Number.parseInt(documentSearchProps.width ?? '445px', 10) || 445;
    const nextLeft = Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8));
    setDocumentSearchPosition({ top: rect.top - 12, left: nextLeft });
  }, [hasDocumentSearch, documentSearchProps.width]);

  const handleArticleClick = useCallback(() => {
    if (isDocumentSearchOpen) setIsDocumentSearchOpen(false);
    else {
      const rect = articleButtonRef.current?.getBoundingClientRect();
      if (rect) {
        const panelWidth = Number.parseInt(documentSearchProps.width ?? '445px', 10) || 445;
        const nextLeft = Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8));
        setDocumentSearchPosition({ top: rect.top - 12, left: nextLeft });
      }
      setIsDocumentSearchOpen(true);
    }
  }, [documentSearchProps.width, isDocumentSearchOpen]);

  useEffect(() => {
    if (!isDocumentSearchOpen) return undefined;
    const updatePosition = () => {
      const rect = articleButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const panelWidth = Number.parseInt(documentSearchProps.width ?? '445px', 10) || 445;
      const nextLeft = Math.max(8, Math.min(rect.left, window.innerWidth - panelWidth - 8));
      setDocumentSearchPosition({ top: rect.top - 12, left: nextLeft });
    };
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [documentSearchProps.width, isDocumentSearchOpen]);

  const handleDocMenuAction = useCallback(
    (selectedValue, doc) => {
      (documentSearchProps.onMenuAction ?? onDocumentSearchMenuChange)?.(selectedValue, doc);
    },
    [documentSearchProps.onMenuAction, onDocumentSearchMenuChange]
  );
  const handleDocDeleteClick = useCallback(
    (doc) => (documentSearchProps.onDeleteClick ?? onDocumentSearchDeleteClick)?.(doc),
    [documentSearchProps.onDeleteClick, onDocumentSearchDeleteClick]
  );
  const handleDocCardClick = useCallback(
    (doc) => {
      (documentSearchProps.onCardClick ?? onDocumentSelect)?.(doc);
      setIsDocumentSearchOpen(false);
    },
    [documentSearchProps.onCardClick, onDocumentSelect]
  );
  const handleDeleteModalOpenChange = useCallback((isOpen) => {
    if (!isOpen) return;
    setIsDocumentSearchOpen(false);
  }, []);

  const rootWrapperClasses = [
    'relative w-full rounded-2xl',
    isProcessing
      ? 'isolate animate-chat-input-shadow-pulse p-[1px] dark:animate-chat-input-shadow-pulse-dark'
      : '',
    isProcessing
      ? `after:pointer-events-none after:absolute after:inset-[-1px] after:-z-10 after:rounded-[17px] after:[background-size:300%_300%] after:content-[""] after:animate-chat-input-gradient ${processingGradientPresetClassName}`
      : '',
  ]
    .filter(Boolean)
    .join(' ');

  const inputShellClasses = [
    'relative z-[1] flex w-full flex-col overflow-visible rounded-2xl bg-surface p-[1px] transition-[border-color,box-shadow,background-color] duration-200',
    isProcessing
      ? 'border border-transparent shadow-none'
      : allowUploadFiles && isDragOverFiles
        ? 'border border-brand-600 shadow-[8px_8px_16px_0px_#00000014] dark:shadow-[8px_8px_16px_0px_rgba(0,0,0,0.45)]'
        : hasContent || isFocused
          ? 'border border-brand-600 shadow-[8px_8px_16px_0px_#00000014] dark:shadow-[8px_8px_16px_0px_rgba(0,0,0,0.45)]'
          : 'border border-[0.5px] border-edge shadow-[8px_8px_16px_0px_#00000014] hover:border-edge-strong dark:shadow-[8px_8px_16px_0px_rgba(0,0,0,0.45)] dark:hover:border-gray-500',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const content = (
    <div
      ref={focusContainerRef}
      onFocus={handleFocus}
      onBlur={handleBlur}
      data-processing={isProcessing}
      data-dragging={allowUploadFiles && isDragOverFiles}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={rootWrapperClasses}
    >
      <div id={id} className={inputShellClasses} {...props}>
        {allowUploadFiles && attachments.length > 0 ? (
          <div className="scrollbar-aimsos-auto-sm flex min-h-[56px] shrink-0 items-center gap-2 overflow-x-auto overflow-y-hidden px-4 py-2">
            {attachments.map((attachment) => (
              <AttachmentItem
                key={attachment.id}
                attachment={attachment}
                onAttachmentRemove={onAttachmentRemove}
              />
            ))}
          </div>
        ) : null}

        <div className="flex min-h-[56px] items-start gap-[10px] p-4">
          <div
            ref={textareaScrollBoxRef}
            className="scrollbar-aimsos-auto-sm min-h-0 max-h-[150px] min-w-0 flex-1 overflow-y-auto"
          >
            <textarea
              ref={textareaRef}
              value={value ?? ''}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
              rows={1}
              aria-label={placeholder}
              {...(hasCharacterLimit ? { maxLength } : {})}
              className="block min-h-6 w-full min-w-0 resize-none border-none bg-transparent text-sm font-normal leading-6 text-content outline-none placeholder:text-content-muted"
            />
          </div>
          <span
            className={[
              'inline-flex',
              hasContent ? 'visible pointer-events-auto' : 'invisible pointer-events-none',
            ].join(' ')}
          >
            <button
              type="button"
              onClick={handleClear}
              aria-label="Limpiar"
              className="inline-flex items-center justify-center text-content-secondary hover:text-content"
            >
              <X size={16} />
            </button>
          </span>
        </div>

        <div className="flex min-h-12 items-center justify-between gap-4 px-4 py-2">
          <div className="flex items-center gap-2">
            <ChatInputAddMenu
              items={addMenuItems}
              disabled={disabled}
              onSelect={handleAddMenuChangeWithMode}
            />

            {hasDocumentSearch ? (
              <Tooltip content={`Created documents (${documents.length})`} arrow={false} align="start" position="bottom" delay={150}>
                <span
                  ref={articleButtonRef}
                  onMouseEnter={handleArticleMouseEnter}
                  className="relative inline-flex"
                >
                  {documents.length > 0 ? (
                    <span className="pointer-events-none absolute right-0 top-0 h-2 w-2 translate-x-[-80%] translate-y-[70%] rounded-full bg-red-500" />
                  ) : null}
                  <button
                    type="button"
                    onClick={handleArticleClick}
                    disabled={disabled}
                    aria-label="Artículo"
                    className="inline-flex rounded-full p-2 text-content-secondary hover:bg-surface-hover"
                  >
                    <FileText size={16} />
                  </button>
                </span>
              </Tooltip>
            ) : null}

            {footerBadges.length > 0
              ? footerBadges.map((badge) => (
                  <Badge
                    key={badge.id}
                    variant={badge.variant ?? 'success'}
                    size="small"
                    closable
                    onClose={badge.onClose}
                    className="!h-6 !px-3 !text-xs !font-medium"
                  >
                    {badge.label}
                  </Badge>
                ))
              : null}

            {selectedAddMode ? (
              <>
                <Badge
                  variant={selectedAddMode.tagColor ?? 'success'}
                  size="small"
                  closable
                  onClose={handleModeTagClose}
                  clickable={clickableTag}
                  onClick={clickableTag && onModeTagClick ? handleModeTagClick : undefined}
                  className="!h-6 !px-3 !text-xs !font-medium"
                >
                  {selectedAddMode.label}
                </Badge>

                {selectedAddMode.menu?.length > 0 ? (
                  <Dropdown
                    label={
                      selectedAddMode.menu.find(
                        (item) =>
                          item.value ===
                          (selectedMenuValues[selectedAddMode.value] ?? selectedAddMode.menu?.[0]?.value)
                      )?.label ??
                      selectedAddMode.menuLabel ??
                      selectedAddMode.menu?.[0]?.label ??
                      ''
                    }
                    variant={selectedAddMode.menuType ?? 'purple'}
                    size="small"
                    pill
                    menuPosition="top"
                    disabled={disabled}
                    className="[&>span>button]:!h-6 [&>span>button]:!px-3 [&>span>button]:!text-xs [&>span>button]:!font-medium"
                  >
                    {selectedAddMode.menu.map((item) => (
                      <Dropdown.Item
                        key={item.value}
                        onClick={() =>
                          handleAddMenuSubmenuChange(selectedAddMode.value, item.value, {
                            label: item.label,
                          })
                        }
                      >
                        {item.label}
                      </Dropdown.Item>
                    ))}
                  </Dropdown>
                ) : null}
              </>
            ) : null}
          </div>

          <div className="flex items-center gap-4">
            {providers != null && providers.length > 0 ? (
              <ChatInputModelSelector
                providers={providers}
                value={modelSelectorValue}
                provider={modelSelectorProvider}
                onChange={onModelChange}
                onProviderChange={onProviderChange}
                disabled={disabled}
                alignMenu={modelAlignMenu}
              />
            ) : null}

            <div className={value?.trim() || isProcessing ? 'visible' : 'invisible pointer-events-none'}>
              <Button
                variant="primary"
                size="sm"
                pill
                onClick={handleActionClick}
                disabled={disabled || (!isProcessing && !value?.trim()) || isAtOrExceededLimit}
                aria-label={isProcessing ? 'Detener' : 'Enviar'}
                className={[
                  '!h-6 !w-6 !min-h-6 !min-w-6 !p-0',
                  '[&_svg]:!size-3 [&_svg]:!text-white',
                  actionButtonClassName,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isProcessing ? <Square size={14} /> : <ArrowUp size={14} />}
              </Button>
            </div>

            {hasCharacterLimit ? (
              <div
                className={[
                  'whitespace-nowrap text-right text-xs leading-6',
                  isAtOrExceededLimit
                    ? 'text-red-600 dark:text-red-400'
                    : isApproachingLimit
                      ? 'text-amber-500 dark:text-amber-400'
                      : 'text-content-muted',
                ].join(' ')}
              >
                {characterCount} of {maxLength}
              </div>
            ) : null}
          </div>
        </div>

        {allowUploadFiles && isDragOverFiles ? (
          <div className="pointer-events-none absolute inset-0 z-[2] flex flex-col items-center justify-center gap-2 rounded-[16px] border border-brand-600 bg-surface/60 p-4 text-center backdrop-blur-[5px] dark:border-brand-500 dark:bg-gray-950/70">
            <Paperclip size={18} className="text-content-secondary" />
            <div className="text-base font-semibold text-content">{dragDropTitle}</div>
            <div className="text-sm font-medium text-content-secondary">{dragDropSubtitleText}</div>
          </div>
        ) : null}
      </div>
    </div>
  );

  const rootNode =
    growDirection === 'up' ? (
      <div ref={rootRef} className="flex h-full min-h-0 w-full flex-col justify-end p-[1px]">
        {content}
      </div>
    ) : (
      <div ref={rootRef}>{content}</div>
    );

  const documentSearchPortal =
    hasDocumentSearch &&
    (isDocumentSearchPreloaded || isDocumentSearchOpen) &&
    createPortal(
      <div
        className={[
          'fixed z-[1000] box-border transition-opacity duration-150',
          isDocumentSearchOpen
            ? 'visible pointer-events-auto opacity-100'
            : 'invisible pointer-events-none opacity-0',
        ].join(' ')}
        style={{
          top: documentSearchPosition.top,
          left: documentSearchPosition.left,
          transform: 'translateY(-100%)',
        }}
        role="dialog"
        aria-modal="false"
      >
        <div ref={documentSearchRef}>
          <ChatInputDocumentSearch
            searchValue={docSearchSearch}
            onSearchChange={setDocSearchSearch}
            documents={documents}
            searchPlaceholder={documentSearchProps.searchPlaceholder}
            emptyTitle={documentSearchProps.emptyTitle}
            emptySubtitle={documentSearchProps.emptySubtitle}
            width={documentSearchProps.width}
            height={documentSearchProps.height}
            maxHeight={documentSearchProps.maxHeight}
            className={documentSearchProps.className}
            onMenuAction={handleDocMenuAction}
            onDeleteClick={handleDocDeleteClick}
            onCardClick={handleDocCardClick}
            onDeleteModalOpenChange={handleDeleteModalOpenChange}
          />
        </div>
      </div>,
      document.body
    );

  return (
    <>
      {documentSearchPortal}
      {allowUploadFiles ? (
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={acceptAttribute}
          onChange={handleFileChange}
          className="hidden"
          aria-hidden
        />
      ) : null}
      {rootNode}
    </>
  );
};

const MemoizedChatInput = memo(ChatInput);

export { CHAT_INPUT_MAX_CHARS, CHAT_INPUT_PROCESSING_GRADIENT_VARIANTS };
export default MemoizedChatInput;
